import {
  COPPEL_BANCO_ID,
  COPPEL_BANCO_IDS_COMPAT,
  COPPEL_BANCO_NOMBRE,
  coppelIntegracionActiva,
} from './constants'
import { destinoSoportaCoppel } from './mappers'

export function idDeOpcionBanco(entry) {
  if (entry == null) return NaN
  if (typeof entry === 'object') {
    const raw = entry.value != null ? entry.value : entry.Id
    return parseInt(raw, 10)
  }
  return parseInt(entry, 10)
}

function labelDeOpcionBanco(entry) {
  if (entry == null || typeof entry !== 'object') return ''
  return String(entry.label != null ? entry.label : entry.Nombre || '').trim()
}

/** ¿Nombre de financiera corresponde a Coppel? */
export function esNombreBancoCoppel(nombre) {
  const n = String(nombre || '')
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
  if (!n) return false
  return n === 'COPPEL' || n === 'BANCOPPEL' || n.includes('COPPEL')
}

/** ¿Id numérico es Coppel (40 preferido u otros compatibles)? */
export function esIdBancoCoppel(bancoId) {
  const id = parseInt(bancoId, 10)
  if (Number.isNaN(id)) return false
  return COPPEL_BANCO_IDS_COMPAT.includes(id)
}

/**
 * Resuelve el id de Coppel desde la lista de bancos del entorno.
 * 1) Opción con label/Nombre "COPPEL" (o BanCoppel).
 * 2) Id preferido 40 si está en la lista.
 * 3) Cualquier id compatible (p. ej. 42 en prueba).
 * 4) Fallback: COPPEL_BANCO_ID (40).
 */
export function resolverIdBancoCoppelDesdeLista(lista) {
  const out = Array.isArray(lista) ? lista : []

  const porNombre = out.find((b) => esNombreBancoCoppel(labelDeOpcionBanco(b)))
  if (porNombre) {
    const id = idDeOpcionBanco(porNombre)
    if (!Number.isNaN(id)) return id
  }

  const porPreferido = out.find((b) => idDeOpcionBanco(b) === COPPEL_BANCO_ID)
  if (porPreferido) return COPPEL_BANCO_ID

  const porCompat = out.find((b) => esIdBancoCoppel(idDeOpcionBanco(b)))
  if (porCompat) return idDeOpcionBanco(porCompat)

  return COPPEL_BANCO_ID
}

/** Opción v-select de Coppel ya presente en la lista (por nombre o id). */
export function encontrarOpcionCoppel(lista) {
  const out = Array.isArray(lista) ? lista : []
  const porNombre = out.find((b) => esNombreBancoCoppel(labelDeOpcionBanco(b)))
  if (porNombre) return porNombre
  return out.find((b) => esIdBancoCoppel(idDeOpcionBanco(b))) || null
}

/** Normaliza ComparaBancos del API (array, string "1,40" o objetos). */
export function normalizarIdsBancosCaso(bancosCaso) {
  if (bancosCaso == null || bancosCaso === '') return []
  if (Array.isArray(bancosCaso)) {
    return [...new Set(bancosCaso.map((x) => idDeOpcionBanco(x)).filter((n) => !Number.isNaN(n)))]
  }
  if (typeof bancosCaso === 'string') {
    return [
      ...new Set(
        bancosCaso
          .split(',')
          .map((s) => parseInt(String(s).trim(), 10))
          .filter((n) => !Number.isNaN(n))
      ),
    ]
  }
  const n = idDeOpcionBanco(bancosCaso)
  return Number.isNaN(n) ? [] : [n]
}

/** Ids numéricos desde la selección del v-select (`caso.bancos`). */
export function idsBancosDesdeSeleccion(seleccion) {
  const ids = normalizarIdsBancosCaso(seleccion)
  if (!coppelIntegracionActiva()) {
    return ids.filter((id) => !esIdBancoCoppel(id))
  }
  return ids
}

/**
 * Agrega Coppel al catálogo de bancos del formulario si aplica (hipotecario + destino soportado).
 * Prefiere el registro real de ListaFinancieras por nombre; si no existe, usa id 40.
 */
export function ensureCoppelEnListaBancos(lista, { precalificacionTipo, destinoId } = {}) {
  if (!coppelIntegracionActiva()) {
    return Array.isArray(lista) ? lista.slice() : []
  }
  if (parseInt(precalificacionTipo, 10) !== 1) {
    return Array.isArray(lista) ? lista.slice() : []
  }
  if (!destinoSoportaCoppel(destinoId)) {
    return Array.isArray(lista) ? lista.slice() : []
  }

  const out = Array.isArray(lista) ? lista.slice() : []
  if (encontrarOpcionCoppel(out)) {
    return out
  }

  const idCoppel = resolverIdBancoCoppelDesdeLista(out)
  out.push({ label: COPPEL_BANCO_NOMBRE, value: idCoppel })
  return out
}

/** Actualiza `vm.bancos` (opciones del v-select) y, si procede, la selección en `vm.caso.bancos`. */
export function aplicarCoppelEnSelectorBancos(vm) {
  if (!coppelIntegracionActiva()) return
  if (!vm || !vm.caso) return

  const destinoId = vm.caso.destino != null ? vm.caso.destino : vm.destino
  const opts = {
    precalificacionTipo: vm.caso.PrecalificacionTipo,
    destinoId,
  }

  const antes = Array.isArray(vm.bancos) ? vm.bancos.length : 0
  const teniaCoppelEnOpciones = !!encontrarOpcionCoppel(vm.bancos)

  vm.bancos = ensureCoppelEnListaBancos(vm.bancos, opts)
  vm.bancos.sort((a, b) =>
    String(a.label || '').localeCompare(String(b.label || ''), 'es', { sensitivity: 'base' })
  )

  const opcionCoppel = encontrarOpcionCoppel(vm.bancos)
  if (!opcionCoppel) return

  const seleccion = Array.isArray(vm.caso.bancos) ? vm.caso.bancos : []
  const seleccionTeniaCoppel = seleccion.some(
    (b) => esIdBancoCoppel(idDeOpcionBanco(b)) || esNombreBancoCoppel(labelDeOpcionBanco(b))
  )

  if (seleccionTeniaCoppel) return

  // Si antes se preseleccionaron todos los bancos del API, incluir Coppel en la selección.
  if (!teniaCoppelEnOpciones && antes > 0 && seleccion.length >= antes) {
    vm.caso.bancos = [...seleccion, opcionCoppel]
  }
}

/**
 * Tras recargar el caso desde BD, vuelve a marcar Coppel si el usuario lo tenía seleccionado.
 * ListaFinancieras a veces no incluye Coppel; sin esto desaparece al guardar y cotizar.
 */
export function restaurarCoppelEnFormulario(vm, bancosIds) {
  if (!coppelIntegracionActiva()) return
  if (!vm || !vm.caso) return

  const ids = normalizarIdsBancosCaso(bancosIds)
  if (!ids.some((id) => esIdBancoCoppel(id))) return

  aplicarCoppelEnSelectorBancos(vm)

  const opcionCoppel = encontrarOpcionCoppel(vm.bancos)
  if (!opcionCoppel) return

  const sel = Array.isArray(vm.caso.bancos) ? vm.caso.bancos.slice() : []
  if (
    sel.some(
      (b) => esIdBancoCoppel(idDeOpcionBanco(b)) || esNombreBancoCoppel(labelDeOpcionBanco(b))
    )
  ) {
    return
  }

  vm.caso.bancos = [...sel, opcionCoppel]
}

/** ¿La selección / lista de ids incluye Coppel? */
export function seleccionIncluyeCoppel(seleccionOIds) {
  return normalizarIdsBancosCaso(seleccionOIds).some((id) => esIdBancoCoppel(id))
}
